import { debounce } from 'lodash';
import { useCallback, useMemo, useState } from 'react';

import Field from 'ui/Field';
import NumberInput from 'ui/NumberInput';

import { DELAYED_INPUT_DEBOUNCE_MS } from '../../../../constants';
import { useSankeyFormContext } from '../../../../contexts/sankeyFormContext';

type Props = {
  label: string;
  field: SankeyFormNumericField;
  canBeEmpty?: boolean;
};

// TODO: Use FieldSet Chakra component
const FlowAmountInput: React.FC<Props> = ({
  label,
  field,
  canBeEmpty = false,
}) => {
  const { form } = useSankeyFormContext();

  // Convert numeric amount to string for the input field, mapping undefined to empty string.
  const [amount, setAmount] = useState(`${form.getValues(field) || ''}`);
  const [amountError, setAmountError] = useState('');

  // Update the error message with the debounce and propagate the new amount to the form only if it's valid.
  const debouncedPropagateAmount = useMemo(
    () =>
      debounce((newAmount: string) => {
        if (newAmount === '') {
          if (canBeEmpty) {
            form.setValue(field, undefined, { shouldDirty: true });
            setAmountError('');
          } else {
            setAmountError('Cannot be empty');
          }
        } else if (isNaN(Number(newAmount))) {
          setAmountError('Not a valid number');
        } else if (Number(newAmount) === 0) {
          setAmountError('Cannot be zero');
        } else if (Number(newAmount) < 0) {
          setAmountError('Cannot be negative');
        } else {
          form.setValue(field, Number(newAmount), { shouldDirty: true });
          setAmountError('');
        }
      }, DELAYED_INPUT_DEBOUNCE_MS),
    [form]
  );

  // Update the flow amount in the temporary variable before propagating it to the form.
  // This lets us validate it before updating the form and it also speeds up editing a lot.
  const handleChange = useCallback(
    ({ value }: { value: string }) => {
      setAmount(value);
      debouncedPropagateAmount(value);
    },
    [debouncedPropagateAmount]
  );

  // TODO: Consider formatting this with amount prefix and suffix.
  // See https://v2.chakra-ui.com/docs/components/number-input/usage#formatting-and-parsing-the-value
  return (
    <Field
      label={label}
      invalid={!!amountError}
      errorText={amountError}
      fontSize="xs"
    >
      <NumberInput value={amount} onValueChange={handleChange} size="sm" />
    </Field>
  );
};

export default FlowAmountInput;
